import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AccessibleModal from './AccessibleModal';
import BadWordsModal from './BadWordsModal';
import {
  popoverContainerWideStyle,
  popoverHeaderStyle,
  popoverLabelStyle,
  popoverInputStyle,
  popoverTextareaStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';
import '../CreatePostPage.css'; // Image upload action-button styles
import '../CreateMarketplaceObjectPage.css'; // Accessible checkbox styles

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Image constants
const MAX_FILES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_DIMENSION = 1920;
const COMPRESSION_QUALITY = 0.85;

/**
 * Shared Edit Marketplace Object Popover Component
 * Used in LandingPage (Marktplatz tab) and MeinMarktplatz for consistent edit
 * functionality. Issue #440: the "Zu verschenken" (free giveaway) checkbox is
 * available here so free giveaways can be edited without being forced to enter
 * a price.
 *
 * @param {Object} object - The marketplace object (post) being edited
 * @param {Function} onClose - Close callback
 * @param {Function} onSave - Called with the updated object on successful save
 */
const EditMarketplaceObjectPopover = ({ object, onClose, onSave }) => {
  const { t } = useLanguage();

  // Field state
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [content, setContent] = useState('');
  const [isFreeGiveaway, setIsFreeGiveaway] = useState(false); // Issue #440

  // Image state
  const [existingImages, setExistingImages] = useState([]);
  const [deleteImageIds, setDeleteImageIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [isCompressing, setIsCompressing] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Issue #161: Bad words modal state
  const [showBadWordsModal, setShowBadWordsModal] = useState(false);
  const [detectedBadWords, setDetectedBadWords] = useState([]);

  const isOffer = object?.marketplace_type === 'offer';

  // Initialize fields and fetch existing images when object changes
  useEffect(() => {
    if (object) {
      setTitle(object.title || '');
      setPrice(object.price || '');
      setContent(object.post_content || '');
      setIsFreeGiveaway(!!object.is_free_giveaway); // Issue #440
      fetchExistingImages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object]);

  const fetchExistingImages = async () => {
    if (!object?.id) return;
    try {
      const response = await axios.get(`/api/posts/${object.id}/images`);
      setExistingImages(response.data || []);
    } catch (err) {
      console.error('Failed to fetch object images:', err);
      setExistingImages([]);
    }
  };

  // Image compression function
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size <= MAX_FILE_SIZE) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width > height) {
              height = (height / width) * MAX_IMAGE_DIMENSION;
              width = MAX_IMAGE_DIMENSION;
            } else {
              width = (width / height) * MAX_IMAGE_DIMENSION;
              height = MAX_IMAGE_DIMENSION;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });

                if (compressedFile.size > MAX_FILE_SIZE) {
                  reject(new Error('Bild ist zu groß (auch nach Komprimierung)'));
                } else {
                  resolve(compressedFile);
                }
              } else {
                reject(new Error('Fehler beim Komprimieren des Bildes'));
              }
            },
            'image/jpeg',
            COMPRESSION_QUALITY
          );
        };
        img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen des Bildes'));
      reader.readAsDataURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);

    const remainingExisting = existingImages.length - deleteImageIds.length;
    const totalImages = remainingExisting + newFiles.length + files.length;

    if (totalImages > MAX_FILES) {
      alert(`Maximal ${MAX_FILES} Bilder pro Post erlaubt`);
      return;
    }

    setIsCompressing(true);

    const validFiles = [];
    const previews = [];

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Ungültiger Dateityp: ${file.name}`);
        continue;
      }
      try {
        const compressedFile = await compressImage(file);
        validFiles.push(compressedFile);
        await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            previews.push({ id: Date.now() + Math.random(), src: event.target.result });
            resolve();
          };
          reader.readAsDataURL(compressedFile);
        });
      } catch (err) {
        console.error('Compression failed:', err);
        validFiles.push(file);
      }
    }

    setNewFiles(prev => [...prev, ...validFiles]);
    setNewPreviews(prev => [...prev, ...previews]);
    setIsCompressing(false);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!title.trim()) { alert(t('marketplace.enterTitle')); return; }
    // Issue #440: price only required for offers that are NOT free giveaways
    if (isOffer && !isFreeGiveaway && !price) { alert(t('marketplace.enterPrice')); return; }
    if (!content.trim()) { alert(t('marketplace.enterDescription')); return; }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('content', content);
      formData.append('title', title);
      formData.append('marketplace_type', object.marketplace_type);

      if (isOffer) {
        // Issue #440: send free giveaway flag; only send price when not free
        formData.append('is_free_giveaway', isFreeGiveaway ? 'true' : 'false');
        if (!isFreeGiveaway && price) {
          formData.append('price', price);
        }
      }

      if (deleteImageIds.length > 0) {
        formData.append('delete_image_ids', deleteImageIds.join(','));
      }
      newFiles.forEach(file => formData.append('images', file));

      const response = await axios.put(`/api/posts/${object.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onSave(response.data);
    } catch (err) {
      console.error('Failed to update marketplace object:', err);
      // Issue #161: Check for bad words in response
      const badWords = err.response?.data?.bad_words;
      if (badWords && badWords.length > 0) {
        setDetectedBadWords(badWords);
        setShowBadWordsModal(true);
      } else {
        alert(err.response?.data?.message || t('marketplace.saveError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!object) return null;

  const isSaveDisabled = !title.trim() || !content.trim() || isSubmitting || isCompressing;

  return (
    <>
      <AccessibleModal
        isOpen={true}
        onClose={onClose}
        titleId="edit-marketplace-modal-title"
        containerStyle={popoverContainerWideStyle}
        canClose={!isSubmitting}
      >
        <h2 id="edit-marketplace-modal-title" style={popoverHeaderStyle}>{t('marketplace.editObject')}</h2>

        {/* Existing Images */}
        {existingImages.length > 0 && (
          <div className="image-previews">
            {existingImages
              .filter(img => !deleteImageIds.includes(img.id))
              .map(img => (
                <div key={img.id} className="preview-item" style={{ position: 'relative' }}>
                  <img
                    src={`${API_BASE_URL}${img.image_url}`}
                    alt="Existing"
                    className="preview-image"
                    style={{ pointerEvents: 'none', position: 'relative', zIndex: 1 }}
                  />
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDeleteImageIds(prev => [...prev, img.id]);
                    }}
                    title={t('marketplace.removeImage') || 'Bild entfernen'}
                    aria-label={t('marketplace.removeImage') || 'Bild entfernen'}
                    style={{ position: 'absolute', top: '4px', right: '4px', zIndex: 9999, pointerEvents: 'auto' }}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* New Image Previews */}
        {newPreviews.length > 0 && (
          <div className="image-previews">
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '8px 0 4px 0' }}>
              {t('marketplace.newImages')}
            </p>
            {newPreviews.map(preview => (
              <div key={preview.id} className="preview-item">
                <img src={preview.src} alt="Preview" className="preview-image" />
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={() => {
                    setNewPreviews(prev => prev.filter(p => p.id !== preview.id));
                    setNewFiles(prev => prev.filter((_, i) => newPreviews[i]?.id !== preview.id));
                  }}
                  title={t('marketplace.removeImage') || 'Bild entfernen'}
                  aria-label={t('marketplace.removeImage') || 'Bild entfernen'}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {/* File Upload - Issue #115: button for keyboard accessibility */}
        <button
          type="button"
          className="create-post-action-button"
          title={isCompressing ? t('marketplace.compressing') : t('marketplace.addImages')}
          style={{ marginBottom: '12px' }}
          onClick={() => document.getElementById('edit-marketplace-file-input')?.click()}
          disabled={isCompressing || isSubmitting}
          aria-label={t('marketplace.addImages') || 'Bilder hinzufügen'}
        >
          <span className="action-button-icon" aria-hidden="true">{isCompressing ? '⏳' : '📷'}</span>
          <span className="action-button-text">{t('marketplace.addImages') || 'Bilder'}</span>
        </button>
        <input
          id="edit-marketplace-file-input"
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          disabled={isCompressing || isSubmitting}
          className="create-post-file-input"
          aria-hidden="true"
        />

        {/* Title Field */}
        <div>
          <label htmlFor="edit-marketplace-title" style={{ ...popoverLabelStyle, display: 'block', marginBottom: '8px' }}>
            {t('marketplace.titleLabel')}
          </label>
          <input
            id="edit-marketplace-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={popoverInputStyle}
          />
        </div>

        {/* Description Field */}
        <div>
          <label htmlFor="edit-marketplace-description" style={{ ...popoverLabelStyle, display: 'block', marginBottom: '8px' }}>
            {t('marketplace.descriptionLabel')}
          </label>
          <textarea
            id="edit-marketplace-description"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            style={{ ...popoverTextareaStyle, overflow: 'hidden', resize: 'none' }}
          />
        </div>

        {/* Issue #440: Free Giveaway Checkbox (only for offers) */}
        {/* paddingLeft override: the shared section adds 16px for the create page
            layout; in the popover it must line up with the other fields */}
        {isOffer && (
          <div className="marketplace-checkbox-section" style={{ paddingLeft: 0 }}>
            <label className="marketplace-checkbox-label">
              <input
                type="checkbox"
                checked={isFreeGiveaway}
                onChange={() => setIsFreeGiveaway(!isFreeGiveaway)}
                className="visually-hidden"
                aria-label={t('marketplace.freeGiveaway') || 'Zu verschenken'}
              />
              <span className={`checkbox-custom ${isFreeGiveaway ? 'checked' : ''}`} aria-hidden="true">
                {isFreeGiveaway && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M1 4L3.5 6.5L9 1" stroke="#F5F5F5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span className="checkbox-text">{t('marketplace.freeGiveaway') || 'Zu verschenken'}</span>
            </label>
          </div>
        )}

        {/* Price Field (only for offers; disabled when free giveaway) */}
        {isOffer && (
          <div style={{ opacity: isFreeGiveaway ? 0.4 : 1, transition: 'opacity 0.2s' }}>
            <label htmlFor="edit-marketplace-price" style={{ ...popoverLabelStyle, display: 'block', marginBottom: '8px' }}>
              {t('marketplace.priceLabel')}
            </label>
            <input
              id="edit-marketplace-price"
              type="number"
              step="0.01"
              min="0"
              value={isFreeGiveaway ? '' : price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={isFreeGiveaway}
              aria-required={!isFreeGiveaway}
              style={popoverInputStyle}
              className="price-input-no-spinner"
            />
          </div>
        )}

        <div style={popoverButtonContainerStyle}>
          <button onClick={onClose} disabled={isSubmitting} style={getCancelButtonStyle(isSubmitting)}>
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaveDisabled}
            style={getSubmitButtonStyle(isSaveDisabled)}
          >
            {isSubmitting ? t('posts.saving') : t('save')}
          </button>
        </div>
      </AccessibleModal>

      {/* Issue #161: Bad words modal */}
      <BadWordsModal
        isOpen={showBadWordsModal}
        onClose={() => setShowBadWordsModal(false)}
        badWords={detectedBadWords}
        contentType="object"
      />
    </>
  );
};

export default EditMarketplaceObjectPopover;
